import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { FileText, Plus, Calendar, Search, Filter, Download, Eye, Edit, ChevronLeft, ChevronRight } from 'lucide-react';

function OrderDetails() {
  const { API, user } = useContext(AppContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newOrder, setNewOrder] = useState({
    customer: '',
    product: '',
    quantity: '',
    deliveryDate: '',
    priority: 'Normal',
    notes: ''
  });

  const months = [
    { value: '2025-02', label: 'February 2025' },
    { value: '2025-01', label: 'January 2025' },
    { value: '2024-12', label: 'December 2024' },
  ];

  useEffect(() => {
    fetchOrders();
  }, [selectedMonth]);

  const fetchOrders = async () => {
    setLoading(true);
    // Mock data
    setOrders([
      { id: 'ORD-2025-001', customer: 'ABC Pharma Ltd.', product: 'Aluminum Strips Grade A', quantity: 5000, unit: 'kg', orderDate: '2025-02-01', deliveryDate: '2025-02-10', status: 'Confirmed', priority: 'High', value: 250000, createdBy: 'Admin' },
      { id: 'ORD-2025-002', customer: 'XYZ Industries', product: 'Zinc Alloy Sheets', quantity: 3500, unit: 'kg', orderDate: '2025-02-02', deliveryDate: '2025-02-12', status: 'Processing', priority: 'Normal', value: 175000, createdBy: 'Finance' },
      { id: 'ORD-2025-003', customer: 'MediCorp India', product: 'Copper Strips', quantity: 2000, unit: 'kg', orderDate: '2025-02-03', deliveryDate: '2025-02-08', status: 'Dispatched', priority: 'Urgent', value: 320000, createdBy: 'Admin' },
      { id: 'ORD-2025-004', customer: 'HealthFirst Inc.', product: 'Aluminum Strips Grade B', quantity: 4500, unit: 'kg', orderDate: '2025-02-05', deliveryDate: '2025-02-15', status: 'Pending', priority: 'Normal', value: 180000, createdBy: 'Finance' },
      { id: 'ORD-2025-005', customer: 'Global Metals', product: 'Lead Sheets', quantity: 1500, unit: 'kg', orderDate: '2025-02-06', deliveryDate: '2025-02-18', status: 'Confirmed', priority: 'Low', value: 95000, createdBy: 'Admin' },
    ]);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Processing': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Dispatched': return 'bg-green-100 text-green-700 border-green-200';
      case 'Pending': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'Delivered': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent': return 'bg-red-100 text-red-700';
      case 'High': return 'bg-orange-100 text-orange-700';
      case 'Normal': return 'bg-blue-100 text-blue-700';
      case 'Low': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleAddOrder = () => {
    const order = {
      id: `ORD-2025-${String(orders.length + 1).padStart(3, '0')}`,
      ...newOrder,
      quantity: parseInt(newOrder.quantity),
      unit: 'kg',
      orderDate: new Date().toISOString().split('T')[0],
      status: 'Pending',
      value: parseInt(newOrder.quantity) * 50,
      createdBy: user?.role || 'Admin'
    };
    setOrders([order, ...orders]);
    setShowAddModal(false);
    setNewOrder({ customer: '', product: '', quantity: '', deliveryDate: '', priority: 'Normal', notes: '' });
  };

  const filteredOrders = orders.filter(order =>
    order.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.product.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalValue = orders.reduce((sum, o) => sum + o.value, 0);
  const totalOrders = orders.length;

  if (loading) {
    return <div className="text-center py-12">Loading orders...</div>;
  }

  return (
    <div className="space-y-6" data-testid="order-details-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
          <p className="text-gray-500 mt-1">Manage and track customer orders</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 hover:bg-gray-50">
            <Download size={18} />
            <span>Export</span>
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md hover:shadow-lg transition-all"
            data-testid="add-order-btn"
          >
            <Plus size={18} />
            <span>Add Order</span>
          </button>
        </div>
      </div>

      {/* Month Selector & Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500 mb-2">Select Month</p>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
            data-testid="month-selector"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="bg-gradient-to-br from-[#1a237e]/5 to-[#283593]/10 rounded-xl p-5 border border-[#1a237e]/20">
          <p className="text-sm text-[#1a237e] font-medium">Total Orders</p>
          <p className="text-3xl font-bold text-[#1a237e]">{totalOrders}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <p className="text-sm text-green-600 font-medium">Total Value</p>
          <p className="text-3xl font-bold text-green-700">₹{(totalValue / 100000).toFixed(2)}L</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <p className="text-sm text-orange-600 font-medium">Pending</p>
          <p className="text-3xl font-bold text-orange-700">{orders.filter(o => o.status === 'Pending').length}</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
          />
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Order ID</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Product</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Quantity</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Value</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Delivery Date</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Priority</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Created By</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 font-mono text-sm text-[#1a237e] font-medium">{order.id}</td>
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{order.customer}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{order.product}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-gray-900">{order.quantity.toLocaleString()} {order.unit}</td>
                  <td className="px-4 py-4 text-sm font-medium text-green-600">₹{order.value.toLocaleString()}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{order.deliveryDate}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
                      {order.priority}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      order.createdBy === 'Admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {order.createdBy}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-1.5 text-gray-400 hover:text-[#1a237e] hover:bg-blue-50 rounded transition">
                        <Eye size={16} />
                      </button>
                      <button className="p-1.5 text-gray-400 hover:text-[#e65100] hover:bg-orange-50 rounded transition">
                        <Edit size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Order Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Order</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={newOrder.customer}
                  onChange={(e) => setNewOrder({...newOrder, customer: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                  placeholder="Enter customer name"
                  data-testid="order-customer-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product *</label>
                <input
                  type="text"
                  value={newOrder.product}
                  onChange={(e) => setNewOrder({...newOrder, product: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                  placeholder="Enter product name"
                  data-testid="order-product-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (kg) *</label>
                  <input
                    type="number"
                    value={newOrder.quantity}
                    onChange={(e) => setNewOrder({...newOrder, quantity: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                    placeholder="0"
                    data-testid="order-quantity-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={newOrder.priority}
                    onChange={(e) => setNewOrder({...newOrder, priority: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date *</label>
                <input
                  type="date"
                  value={newOrder.deliveryDate}
                  onChange={(e) => setNewOrder({...newOrder, deliveryDate: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
                  data-testid="order-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={newOrder.notes}
                  onChange={(e) => setNewOrder({...newOrder, notes: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none resize-none"
                  rows={2}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddOrder}
                disabled={!newOrder.customer || !newOrder.product || !newOrder.quantity || !newOrder.deliveryDate}
                className="px-4 py-2.5 bg-gradient-to-r from-[#1a237e] to-[#283593] text-white rounded-xl font-medium shadow-md disabled:opacity-50"
                data-testid="submit-order-btn"
              >
                Add Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderDetails;
